import React, { useCallback, useRef, useEffect, useLayoutEffect, useState } from 'react';
import { TabState, DerivedDataSet, DatasetId } from '../../../../types';
import type { SQLEditorTabConfig } from '../../../../types/tabs';
import { checkDatasetNameDuplicate, DuplicateCheckResult } from '../../../utils/duplicateNameChecker';
import { DuplicateNameDialog } from '../../DuplicateNameDialog';
import { useProjectData } from "../../../stores/hooks/useProjectData"
import { usePaneLayout } from "../../../stores/hooks/usePaneLayout"
import { useTabOperations } from "../../../hooks/useTabOperations"
import { useTabState } from './hooks/useTabState';
import { createDataTableTab, createProjectLineageTab } from '../../../tab-system/TabRegistry';
import { executeOperation } from '../../../utils/executeOperation';
import { duckdbProxy, DuckDBCancelledError } from '../../../utils/duckdb';
import { createDatasetId, createTransformOperationId } from '../../../../types';
import logger from '../../../utils/logger';
import * as styles from './SQLEditorTabContent.css.ts';
import { EditorView, keymap } from '@codemirror/view';
import { EditorState, Compartment, Prec } from '@codemirror/state';
import { sql } from '@codemirror/lang-sql';
import { autocompletion } from '@codemirror/autocomplete';
import { defaultKeymap, indentWithTab } from '@codemirror/commands';
import { searchKeymap } from '@codemirror/search';

/** SQLEditorTabConfigのデフォルト値 */
const defaultSQLEditorConfig: SQLEditorTabConfig = {
  datasetId: undefined,
  query: '',
  outputName: 'Query Result',
  isExecuting: false,
  error: ''
};

/**
 * SQLEditorTabContent
 *
 * DuckDB-based SQL query editor
 * Standard SQL query editor with DuckDB support
 */

interface SQLEditorTabContentProps {
  tab: TabState;
}

export const SQLEditorTabContent: React.FC<SQLEditorTabContentProps> = ({ tab }) => {
  const { project, datasets, addDerivedDataset, removeDataset } = useProjectData();
  const paneLayout = usePaneLayout();
  const tabOperations = useTabOperations({
    project,
    layout: {
      rootPane: paneLayout.layout.rootPane,
      activeContainerId: paneLayout.layout.activeContainerId ?? null
    },
    addTab: paneLayout.addTab,
    addTabWithOptions: paneLayout.addTabWithOptions,
    closeTab: paneLayout.closeTab,
    moveTabWithinPane: paneLayout.moveTabWithinPane,
    moveTabToPane: paneLayout.moveTabToPane,
    startDrag: paneLayout.startDrag,
    endDrag: paneLayout.endDrag
  });

  // useTabStateで状態管理（pane split対応）
  const [config, setConfig] = useTabState<SQLEditorTabConfig>(tab, defaultSQLEditorConfig);

  // 状態を分解して使いやすくする
  const { query, outputName, isExecuting = false, error = '' } = config;

  // 状態更新ヘルパー
  const updateConfig = useCallback((updates: Partial<SQLEditorTabConfig>) => {
    setConfig(prev => ({ ...prev, ...updates }));
  }, [setConfig]);

  // ローカル状態（UIのみに関連）
  const [duplicateDialogResult, setDuplicateDialogResult] = useState<DuplicateCheckResult | null>(null);
  const [pendingDatasetAction, setPendingDatasetAction] = useState<(() => void) | null>(null);
  const [previewData, setPreviewData] = useState<DerivedDataSet | null>(null);
  const [isReinitializing, setIsReinitializing] = useState(false);
  const editorContainerRef = useRef<HTMLDivElement>(null);
  const editorViewRef = useRef<EditorView | null>(null);
  const editableCompartment = useRef(new Compartment());
  const executeQueryRef = useRef<(() => void) | null>(null);
  const updateConfigRef = useRef(updateConfig);

  // クエリの実行
  const executeQuery = useCallback(async () => {
    if (!query.trim()) {
      updateConfig({ error: 'Please enter a query' });
      return;
    }

    // targetDatasetIdとして最初のデータセットを使用
    // （実際のクエリ実行では、FROM句で指定されたテーブルが使われる）
    const selectedDatasetId = Object.keys(datasets)[0] as DatasetId | undefined;
    if (!selectedDatasetId) {
      updateConfig({ error: 'No dataset available' });
      return;
    }

    updateConfig({ isExecuting: true, error: '' });
    setPreviewData(null);

    try {
      const dataset = datasets[selectedDatasetId];
      if (!dataset) {
        throw new Error('Selected dataset not found');
      }

      // DuckDBでSQLクエリを実行
      const result = await executeOperation(
        {
          id: createTransformOperationId(`transform-${crypto.randomUUID()}`),
          type: 'sql_query',
          query: query,
          targetDatasetId: selectedDatasetId
        },
        datasets,
        selectedDatasetId
      );

      // executeSQL内で抽出されたparentDatasetIdsを使用
      const parentDatasetIds = result.parentDatasetIds;

      logger.debug('[SQLEditorTabContent] Query executed with parent datasets', {
        query,
        parentDatasetIds
      });

      // プレビュー用のデータセットを作成
      // dataのみ保持し、DuckDBへの登録は不要（クエリ結果は既にメモリにある）
      const timestamp = Date.now();

      const derivedDataset: DerivedDataSet = {
        id: createDatasetId(`query_${timestamp}`),
        name: outputName || 'Query Result',
        type: 'derived',
        schema: result.schema,
        data: result.data,
        rowCount: result.rowCount,
        parentDatasetIds: parentDatasetIds,
        operation: {
          id: createTransformOperationId(`sql_query_${timestamp}`),
          type: 'sql_query',
          query: query,
          targetDatasetId: selectedDatasetId
        },
        metadata: {
          createdAt: new Date().toISOString(),
          basedOn: parentDatasetIds
        }
      };

      setPreviewData(derivedDataset);
      updateConfig({ lastExecutionTime: new Date().toISOString() });
    } catch (err) {
      // キャンセルの場合はエラーメッセージを表示しない
      if (err instanceof DuckDBCancelledError) {
        logger.info('[SQLEditorTabContent] Query cancelled by user');
        return;
      }

      if (err instanceof Error) {
        updateConfig({ error: err.message });
      } else {
        updateConfig({ error: 'An unknown error occurred' });
      }
      setPreviewData(null);
    } finally {
      updateConfig({ isExecuting: false });
    }
  }, [query, datasets, updateConfig, outputName]);

  // クエリのキャンセル
  const cancelQuery = useCallback(async () => {
    if (!isExecuting) return;

    logger.info('[SQLEditorTabContent] Cancelling query...');
    setIsReinitializing(true);

    try {
      await duckdbProxy.cancelAndReinitialize();
      logger.info('[SQLEditorTabContent] DuckDB reinitialized after cancel');
    } catch (err) {
      logger.error('[SQLEditorTabContent] Failed to reinitialize after cancel', { error: err });
      updateConfig({ error: 'Failed to reinitialize DuckDB after cancel' });
    } finally {
      setIsReinitializing(false);
    }
  }, [isExecuting, updateConfig]);

  // refをレンダリング中に更新（refの更新は副作用ではないので問題ない）
  updateConfigRef.current = updateConfig;
  executeQueryRef.current = executeQuery;

  // 初期化フラグ
  const initializedRef = useRef(false);

  // メタデータから初期値を設定（初回マウント時のみ）
  // tabViewStateが優先され、なければmetadataから初期化
  useEffect(() => {
    if (initializedRef.current) return;

    // tabViewStateが既にある場合は初期化不要（useTabStateが処理済み）
    if (tab.tabViewState) {
      initializedRef.current = true;
      return;
    }

    const initialQuery = tab.metadata?.initialQuery as string | undefined;
    const initialOutputName = tab.metadata?.initialOutputName as string | undefined;
    const initialDatasetId = tab.metadata?.initialDatasetId as DatasetId | undefined;

    const updates: Partial<SQLEditorTabConfig> = {};

    if (initialQuery) {
      updates.query = initialQuery;
    }

    if (initialOutputName) {
      updates.outputName = initialOutputName;
    }

    if (initialDatasetId) {
      updates.datasetId = initialDatasetId;
    }

    if (Object.keys(updates).length > 0) {
      updateConfig(updates);
      logger.info('[SQLEditorTabContent] Initialized with metadata', updates);
    }

    initializedRef.current = true;
  }, [tab.metadata, tab.tabViewState, updateConfig]);

  // CodeMirrorエディタの初期化（一度だけ）
  useLayoutEffect(() => {
    if (!editorContainerRef.current || editorViewRef.current) return;

    // データセット名の補完候補を生成（動的に取得）
    const getDatasetCompletions = () => {
      return Object.values(datasets).map(dataset => ({
        label: dataset.name,
        type: 'table',
        apply: `"${dataset.name}"`
      }));
    };

    // SQL補完関数
    const sqlAutocompletion = autocompletion({
      override: [
        (context) => {
          const word = context.matchBefore(/\w*/);
          if (!word || (word.from === word.to && !context.explicit)) return null;

          return {
            from: word.from,
            options: [
              ...getDatasetCompletions(),
              // SQL keywords
              { label: 'SELECT', type: 'keyword' },
              { label: 'FROM', type: 'keyword' },
              { label: 'WHERE', type: 'keyword' },
              { label: 'GROUP BY', type: 'keyword' },
              { label: 'ORDER BY', type: 'keyword' },
              { label: 'LIMIT', type: 'keyword' },
              { label: 'JOIN', type: 'keyword' },
              { label: 'LEFT JOIN', type: 'keyword' },
              { label: 'RIGHT JOIN', type: 'keyword' },
              { label: 'INNER JOIN', type: 'keyword' },
              { label: 'OUTER JOIN', type: 'keyword' },
              { label: 'ON', type: 'keyword' },
              { label: 'AND', type: 'keyword' },
              { label: 'OR', type: 'keyword' },
              { label: 'AS', type: 'keyword' },
              { label: 'COUNT', type: 'function' },
              { label: 'SUM', type: 'function' },
              { label: 'AVG', type: 'function' },
              { label: 'MIN', type: 'function' },
              { label: 'MAX', type: 'function' },
            ]
          };
        }
      ]
    });

    // Cmd/Ctrl + Enterで実行するキーバインディング（最高優先順位）
    const executeKeymap = Prec.highest(keymap.of([
      {
        key: 'Mod-Enter',
        run: () => {
          // refを経由して常に最新のexecuteQueryを呼び出す
          if (executeQueryRef.current) {
            executeQueryRef.current();
            return true;
          }
          return false;
        }
      }
    ]));

    // エディタの状態を作成
    const startState = EditorState.create({
      doc: query || '',
      extensions: [
        sql(),
        sqlAutocompletion,
        executeKeymap, // 最高優先順位で配置
        keymap.of([...defaultKeymap, ...searchKeymap, indentWithTab]),
        EditorView.updateListener.of((update) => {
          if (update.docChanged) {
            const newQuery = update.state.doc.toString();
            updateConfigRef.current({ query: newQuery });
          }
        }),
        EditorView.theme({
          "&": {
            fontSize: "14px",
            border: "none",
            height: "100%",
          },
          ".cm-scroller": {
            fontFamily: "'Monaco', 'Consolas', 'Courier New', monospace",
            lineHeight: "1.6",
          },
          ".cm-content": {
            minHeight: "200px",
            padding: "12px",
          },
          "&.cm-focused": {
            outline: "none"
          }
        }),
        editableCompartment.current.of(EditorView.editable.of(!isExecuting)),
      ]
    });

    // エディタビューを作成
    const view = new EditorView({
      state: startState,
      parent: editorContainerRef.current
    });

    editorViewRef.current = view;

    // クリーンアップ
    return () => {
      if (editorViewRef.current) {
        editorViewRef.current.destroy();
        editorViewRef.current = null;
      }
    };
    // query、isExecuting、datasetListは初期値として使用するのみで、変更は別のuseEffectで処理
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 空の依存配列で一度だけ初期化

  useEffect(() => {
    if (!editorViewRef.current) return;

    editorViewRef.current.dispatch({
      effects: editableCompartment.current.reconfigure(EditorView.editable.of(!isExecuting))
    });
  }, [isExecuting]);

  // クエリ状態が変更されたらCodeMirrorのドキュメントを更新
  useEffect(() => {
    if (!editorViewRef.current) return;

    const currentDoc = editorViewRef.current.state.doc.toString();
    if (currentDoc !== query) {
      editorViewRef.current.dispatch({
        changes: {
          from: 0,
          to: currentDoc.length,
          insert: query
        }
      });
    }
  }, [query]);

  // 結果を保存
  const handleSaveResult = useCallback(() => {
    if (!previewData || !outputName) return;

    const createDataset = () => {
      // Issue #172: dataを含めずにschema + operationでDerivedDataSetを作成
      // executeOperationが自動的にdataを再生成する
      const derivedDataset: DerivedDataSet = {
        id: previewData.id,
        name: outputName,
        type: 'derived',
        schema: previewData.schema,  // schemaは保持
        // dataは含めない（executeOperationで再生成される）
        parentDatasetIds: previewData.parentDatasetIds,
        operation: previewData.operation,
        metadata: previewData.metadata
      };

      logger.info('[SQLEditorTabContent] Creating dataset:', {
        datasetId: derivedDataset.id,
        name: derivedDataset.name
      });

      addDerivedDataset(derivedDataset);
      setPreviewData(null);

      // 新しいデータセットのタブを開く
      const newTab = createDataTableTab(derivedDataset.id, derivedDataset.name);
      const activeContainerId = tabOperations.getActiveContainerId();

      if (activeContainerId) {
        paneLayout.addTab(activeContainerId, newTab);
        paneLayout.closeTab(activeContainerId, tab.id);
      }
    };

    if (!project) {
      logger.warn('Cannot save dataset: No project loaded');
      updateConfig({ error: 'No project loaded. Please create or open a project first.' });
      return;
    }

    const checkResult = checkDatasetNameDuplicate(outputName, project, paneLayout.layout);

    if (checkResult.isDuplicate) {
      setDuplicateDialogResult(checkResult);
      setPendingDatasetAction(() => createDataset);
    } else {
      createDataset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewData, outputName, project?.id, addDerivedDataset, updateConfig, tabOperations, paneLayout, tab.id]);

  // Project Lineageタブを開く
  const handleViewLineage = useCallback(() => {
    const projectLineageTab = createProjectLineageTab();
    const activeContainerId = tabOperations.getActiveContainerId();
    if (activeContainerId) {
      paneLayout.addTab(activeContainerId, projectLineageTab);
    }
    // ダイアログを閉じる
    setDuplicateDialogResult(null);
  }, [tabOperations, paneLayout]);

  const getPreviewRowCount = () => {
    if (!previewData || !previewData.data) return 0;
    const firstColumn = Object.values(previewData.data)[0];
    return firstColumn ? firstColumn.length : 0;
  };

  return (
    <div
      className={styles.container}
    >
      <div className={styles.header}>
        <h2>SQL Query Editor</h2>
      </div>

      <div className={styles.queryEditor}>
        <label htmlFor="query">SQL Query:</label>
        <div className={styles.editorWrapper} ref={editorContainerRef} />
        <div className={styles.helpText}>
          <p>Write a SQL query using the dataset name in FROM clause. Table name matches the dataset name.</p>
          <p>Example: <code>SELECT column1, AVG(column2) FROM "dataset-name" WHERE condition GROUP BY column1</code></p>
        </div>
      </div>

      <div className={styles.queryActions}>
        {isExecuting ? (
          <button
            onClick={cancelQuery}
            disabled={isReinitializing}
            className={styles.cancelButton}
          >
            {isReinitializing ? 'Reinitializing...' : 'Cancel Query'}
          </button>
        ) : (
          <button
            onClick={executeQuery}
            disabled={!query.trim()}
            className={styles.executeButton}
          >
            Execute Query
          </button>
        )}
        <span className={styles.shortcutHint}>
          Press <kbd>Cmd/Ctrl + Enter</kbd> to execute
        </span>
      </div>

      {error && (
        <div className={styles.error}>
          {error}
        </div>
      )}

      {previewData && (
        <div className={styles.preview}>
          <div className={styles.previewHeader}>
            <h3>Query Result ({getPreviewRowCount()} rows)</h3>
          </div>

          <div className={styles.previewTable}>
            <table>
              <thead>
                <tr>
                  {previewData.schema.columns.map(col => (
                    <th key={col.id}>{col.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: Math.min(10, getPreviewRowCount()) }).map((_, rowIndex) => (
                  <tr key={rowIndex}>
                    {previewData.schema.columns.map(col => (
                      <td key={col.id}>
                        {previewData.data?.[col.id]?.[rowIndex]?.toString() || ''}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {getPreviewRowCount() > 10 && (
              <div className={styles.moreRows}>
                ... and {getPreviewRowCount() - 10} more rows
              </div>
            )}
          </div>

          <div className={styles.saveControls}>
            <div className={styles.outputName}>
              <label htmlFor="outputName">Output Name:</label>
              <input
                id="outputName"
                type="text"
                value={outputName}
                onChange={(e) => updateConfig({ outputName: e.target.value })}
                placeholder="Enter output dataset name"
              />
            </div>
            <button
              onClick={handleSaveResult}
              disabled={!outputName.trim()}
              className={styles.saveButton}
            >
              Save as Dataset
            </button>
          </div>
        </div>
      )}

      {duplicateDialogResult && (
        <DuplicateNameDialog
          result={duplicateDialogResult}
          onCancel={() => {
            setDuplicateDialogResult(null);
            setPendingDatasetAction(null);
          }}
          onViewLineage={duplicateDialogResult.dependencies?.hasAnyDependencies ? handleViewLineage : undefined}
          onConfirm={(newName: string, action: 'rename' | 'increment' | 'replace') => {
            if (action === 'replace') {
              // 既存のデータセットを削除
              if (project) {
                const existingDataset = Object.values(project.datasets).find(
                  dataset => dataset.name === duplicateDialogResult.originalName
                );
                if (existingDataset) {
                  logger.info('[SQLEditorTabContent] Replacing existing dataset:', {
                    datasetId: existingDataset.id,
                    name: existingDataset.name
                  });
                  removeDataset(existingDataset.id);
                }
              }
              // 新しいデータセットを作成
              setDuplicateDialogResult(null);
              if (pendingDatasetAction) {
                pendingDatasetAction();
              }
            } else {
              // renameまたはincrementの場合は名前を更新して再実行
              updateConfig({ outputName: newName });
              setDuplicateDialogResult(null);
              if (pendingDatasetAction) {
                setTimeout(() => handleSaveResult(), 0);
              }
            }
          }}
        />
      )}
    </div>
  );
};
